import React, { useState, useEffect } from 'react';

import { Box, makeStyles } from '@material-ui/core';
import moment from 'moment';

import DatePicker from './DatePicker';
import { GRANULARITY_VALUES, TIME_PERIOD_VALUES } from '../../constants/observations';

const useStyles = makeStyles((theme) => ({
  '& .MuiInputBase-input': {
    padding: '0 16px',
  },
  '& .makeStyles-dateContainer': {
    marginTop: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
  },
  datePickers: {
    '& .NativeDatepicker, .NativeDatepicker .MuiBox-root, & select': {
      height: 30,
      width: 140,
    },
    '&:hover>.MuiSvgIcon-fontSizeSmall': {
      display: 'block !important',
    },
    alignItems: 'center',
    display: 'flex',
    gap: '10px',
    marginLeft: '10px',
  },
  formControl: {
    minWidth: 150,
  },
  granularityRow: {
    alignItems: 'center',
    display: 'flex',
    marginLeft: 0,
  },
  inputContainer: {
    '& .NativeDatepicker, .NativeDatepicker .MuiBox-root, & select': {
      height: 30,
      width: 140,
    },
    '&:hover>.MuiSvgIcon-fontSizeSmall': {
      display: 'block !important',
    },
    position: 'relative',
  },
  label: {
    color: '#172B4D',
    fontSize: '14px',
    fontWeight: 600,
    marginRight: theme.spacing(1),
  },
  periodRow: {
    alignItems: 'center',
    display: 'flex',
  },
  select: {
    '& .MuiInputBase-input': {
      color: '#999',
      fontFamily: 'Roboto',
      fontSize: 13,
      height: '30px',
      padding: '0 5px',
    },
    '&:focus': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      outline: 'none',
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      cursor: 'pointer',
    },
    border: '1px solid #C8C8C8',
    borderRadius: '8px',
    color: '#999',
    height: '30px',
    overflow: 'hidden',
    width: '127px',
  },
}));

const TimePeriodSelector = ({
  avoidWeekly = false,
  onUpdate,
  timeGranularity = GRANULARITY_VALUES.MONTHLY,
  onGranularityChange,
  hideCustomOption = false,
  hideGranularity = false,
}) => {
  const classes = useStyles();
  const [timePeriod, setTimePeriod] = useState(TIME_PERIOD_VALUES.THIS_YEAR);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    updateDateRange(timePeriod);
  }, []);

  const updateDateRange = (period) => {
    let start = '';
    let end = '';

    const now = moment();
    const format = 'MM-DD-YYYY';

    switch (period) {
      case TIME_PERIOD_VALUES.ONE_WEEK:
        start = moment().subtract(1, 'weeks').format(format);
        end = now.format(format);
        break;
      case TIME_PERIOD_VALUES.ONE_MONTH:
        start = moment().subtract(1, 'months').format(format);
        end = now.format(format);
        break;
      case TIME_PERIOD_VALUES.THIS_YEAR:
        start = moment().startOf('year').format(format);
        end = now.format(format);
        break;
      case TIME_PERIOD_VALUES.ONE_YEAR:
        start = moment().subtract(1, 'years').format(format);
        end = now.format(format);
        break;
      case TIME_PERIOD_VALUES.CUSTOM:
        return;
      default:
        break;
    }

    setStartDate(start);
    setEndDate(end);

    if (onUpdate) {
      onUpdate(start, end);
    }
  };

  const handlePeriodChange = (event) => {
    const selectedPeriod = event.target.value;
    setTimePeriod(selectedPeriod);

    if (selectedPeriod !== TIME_PERIOD_VALUES.CUSTOM) {
      updateDateRange(selectedPeriod);

      if (onGranularityChange) {
        let suggestedGranularity;

        switch (selectedPeriod) {
          case TIME_PERIOD_VALUES.ONE_WEEK:
            suggestedGranularity = GRANULARITY_VALUES.DAILY;
            break;
          case TIME_PERIOD_VALUES.ONE_MONTH:
            suggestedGranularity = GRANULARITY_VALUES.DAILY;
            break;
          case TIME_PERIOD_VALUES.THIS_YEAR:
            suggestedGranularity = GRANULARITY_VALUES.MONTHLY;
            break;
          case TIME_PERIOD_VALUES.ONE_YEAR:
            suggestedGranularity = GRANULARITY_VALUES.MONTHLY;
            break;
          default:
            suggestedGranularity = GRANULARITY_VALUES.MONTHLY;
        }

        onGranularityChange(suggestedGranularity);
      }
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (onUpdate) {
      onUpdate(date, endDate);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    if (onUpdate) {
      onUpdate(startDate, date);
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.periodRow}>
        <span className={classes.label}>Time Period:</span>
        <div className={classes.inputContainer}>
          <select aria-label="Time Period" name="timePeriod" className={classes.select} value={timePeriod} onChange={handlePeriodChange}>
            <option value={TIME_PERIOD_VALUES.ONE_WEEK}>1 Week</option>
            <option value={TIME_PERIOD_VALUES.ONE_MONTH}>1 Month</option>
            <option value={TIME_PERIOD_VALUES.THIS_YEAR}>This Year</option>
            <option value={TIME_PERIOD_VALUES.ONE_YEAR}>1 Year</option>
            {!hideCustomOption && <option value={TIME_PERIOD_VALUES.CUSTOM}>Custom</option>}
          </select>
        </div>

        {timePeriod === TIME_PERIOD_VALUES.CUSTOM && (
          <Box className={classes.datePickers}>
            <DatePicker displayDate={startDate} onChange={handleStartDateChange} defaultText="Start Date" />
            <DatePicker displayDate={endDate} onChange={handleEndDateChange} defaultText="End Date" />
          </Box>
        )}
      </Box>

      {!hideGranularity && (
        <Box className={classes.granularityRow}>
          <span className={classes.label}>Time Granularity:</span>
          <div className={classes.inputContainer}>
            <select
              aria-label="Time Granularity"
              name="timeGranularity"
              className={classes.select}
              value={timeGranularity}
              onChange={(e) => onGranularityChange && onGranularityChange(e.target.value)}>
              <option value={GRANULARITY_VALUES.DAILY}>Daily</option>
              {!avoidWeekly && <option value={GRANULARITY_VALUES.WEEKLY}>Weekly</option>}
              <option value={GRANULARITY_VALUES.MONTHLY}>Monthly</option>
              <option value={GRANULARITY_VALUES.YEARLY}>Yearly</option>
            </select>
          </div>
        </Box>
      )}
    </Box>
  );
};

export default TimePeriodSelector;
